import * as React from "react"
import type { ApolloError } from "@apollo/client"
import {
  type EntityParty,
  type InputUpdateFields,
  type TaxStatus,
  type Transaction,
  TransactionType,
  useCreatePartiesMutation,
  useUpdateTransactionFieldsMutation,
} from "@digits-graphql/frontend/graphql-bearer"
import { GRPCErrorCode } from "@digits-shared/grpc/codes"
import errorHelper from "@digits-shared/helpers/errorHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import {
  type BulkUpdate,
  entryTypeFromDisplayType,
  type TransactionSelections,
  type TransactionUpdate,
  type TransactionUpdates,
} from "src/frontend/components/Shared/Transactions/types"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import type FrontendSession from "src/frontend/session"
import { isNewEntityId } from "src/shared/components/Typeahead/sharedTypeahead"

export function useApplyTransactionUpdates() {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const viewKey = useViewVersion()
  const [createParties] = useCreatePartiesMutation()
  const [updateTransactions] = useUpdateTransactionFieldsMutation()
  const { alertSuccess, alertError } = useAlerts()

  return React.useCallback(
    async (transactionUpdates: TransactionUpdates) => {
      const partyNames = findNewParties(Array.from(transactionUpdates.values()))
      const newParties =
        partyNames.length > 0
          ? (await createParties({ variables: { legalEntityId, partyNames } })).data?.createParties
          : undefined

      return updateTransactions({
        variables: {
          legalEntityId,
          viewKey,
          updates: formatTransactionUpdates(transactionUpdates, newParties),
        },
      })
        .then(() => {
          alertSuccess(transactionUpdates.size)
        })
        .catch((err) => alertError(err))
    },
    [alertError, alertSuccess, createParties, legalEntityId, updateTransactions, viewKey]
  )
}

export function useApplyBulkUpdates() {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const viewKey = useViewVersion()
  const [createParties] = useCreatePartiesMutation()
  const [updateTransactions] = useUpdateTransactionFieldsMutation()
  const { alertSuccess, alertError } = useAlerts()

  return React.useCallback(
    async (selections: TransactionSelections, update: BulkUpdate) => {
      const partyNames = findNewParties(update)
      const newParties =
        partyNames.length > 0
          ? (await createParties({ variables: { legalEntityId, partyNames } })).data?.createParties
          : undefined

      return updateTransactions({
        variables: {
          legalEntityId,
          viewKey,
          updates: formatBulkUpdates(selections, update, newParties),
        },
      })
        .then(() => {
          alertSuccess(selections.size)
        })
        .catch((err) => alertError(err))
    },
    [alertError, alertSuccess, createParties, legalEntityId, updateTransactions, viewKey]
  )
}

function useAlerts() {
  const { alertError, alertSuccess } = useChromeAlerts()
  return {
    alertSuccess: (numUpdates: number) => {
      alertSuccess(
        `${numUpdates} ${stringHelper.pluralize(numUpdates, "transaction", "transactions")} updated`
      )
    },
    alertError: (err: ApolloError) => {
      alertError("Error updating transactions", {
        description:
          errorHelper.firstErrorCode(err) === GRPCErrorCode.FailedPrecondition
            ? "Some transactions have pending updates"
            : "",
      })
    },
  }
}

function findNewParties(update: TransactionUpdate[] | BulkUpdate) {
  const updates = Array.isArray(update) ? update : [update]
  const newParties = new Set<string>()
  updates.forEach((u) => {
    if (u.party && isNewEntityId(u.party.id)) {
      newParties.add(u.party.name)
    }
  })
  return Array.from(newParties)
}

function formatTransactionUpdates(
  transactionUpdates: TransactionUpdates,
  newParties?: EntityParty[]
): InputUpdateFields[] {
  const items = Array.from(transactionUpdates).map(
    ([transactionFactId, { transaction, party, category, splitCategory, taxStatus }]) => ({
      transactionFactId,
      transaction,
      party,
      category,
      splitCategory,
      taxStatus,
    })
  )
  return formatUpdates(items, newParties)
}

function formatBulkUpdates(
  selections: TransactionSelections,
  update: BulkUpdate,
  newParties?: EntityParty[]
): InputUpdateFields[] {
  const items = Array.from(selections).map(([transactionFactId, transaction]) => ({
    transactionFactId,
    transaction,
    party: update.party,
    category: update.category,
    splitCategory: update.splitCategory,
    taxStatus: update.taxStatus,
  }))
  return formatUpdates(items, newParties)
}

function formatUpdates(
  items: Array<{
    transactionFactId: string
    transaction: Transaction
    party?: { id: string; name: string } | null
    category?: { id: string } | null
    splitCategory?: { id: string } | null
    taxStatus?: TaxStatus | null
  }>,
  newParties?: EntityParty[]
): InputUpdateFields[] {
  // Map of new party names to their ids
  const newPartyMap = new Map<string, string>(newParties?.map((party) => [party.name, party.id]))

  return items.map(
    ({ transactionFactId, transaction, party, category, splitCategory, taxStatus }) => ({
      transactionFactId,
      entryType: entryTypeFromDisplayType(transaction.displayEntry),
      digitsTransactionType: transaction.digitsTransactionType || TransactionType.Unknown,
      categoryId: category?.id,
      partyId: party && isNewEntityId(party.id) ? newPartyMap.get(party.name) : party?.id,
      splitCategoryId: splitCategory?.id,
      taxStatus: taxStatus,
    })
  )
}
